/**
 * Pure normalization for the `ServiceEditView` template-pricing seam:
 * `GET /templates/:specialistId/service/:masterServiceId/overrides`.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/serviceOverrideUsage.test.mjs`, mirroring `templateOverrides.js`.
 * Do not add imports; in particular not `catalogValidation.js`, which pulls in
 * axios and cannot load under node:test — the SIZE_LABELS/EDIT_SIZE_ORDER
 * copies below are a small intentional duplicate of the ones there, not a
 * shared source, kept only so `describeOverriddenCells` stays pure.
 *
 * `templatePricingService.getServiceOverrideUsage` is the only production
 * caller: it does the actual HTTP GET and the one-time debug log, then hands
 * the raw body to `unwrapTemplatesList` and each row to
 * `normalizeTemplateOverrideRow`. Kept here instead of inline in that file so
 * both can be exercised by node:test without touching axios.
 */

/**
 * Find the templates array inside a response body.
 *
 * THROWS on anything that is not recognizably a templates list. A bare `[]`
 * or a `templates` array (at any of this repo's known wrapping depths,
 * including an explicitly empty one) resolves normally — that is the genuine
 * "checked, no overrides" case. Everything else — `{}`, `null`, `undefined`,
 * `{ result: [...] }`, `{ templates: null }`, `{ templates: "none" }` — throws.
 *
 * This is deliberate: the one unacceptable failure for this seam is a
 * malformed-but-200 response silently rendering as "no overrides in any
 * template", which the UI states as "a price change here reaches every
 * dealership" — a false safety claim. Letting an unrecognized envelope throw,
 * and letting `getServiceOverrideUsage` NOT catch it, means it takes the same
 * "could not check" path as a network failure instead.
 */
export function unwrapTemplatesList(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.templates)) return data.templates;
    if (data.data && typeof data.data === 'object' && Array.isArray(data.data.templates)) {
      return data.data.templates;
    }
    if (Array.isArray(data.data)) return data.data;
  }
  throw new Error(
    'getServiceOverrideUsage: response body did not carry a recognizable templates list'
  );
}

const num = (v) => {
  const n = Number(v);
  return v != null && Number.isFinite(n) ? n : null;
};

/**
 * One server row -> the shape `ServiceEditView` reads. Every field is read
 * defensively — the payload has not been observed from this repo, only
 * described in a written reply.
 */
export function normalizeTemplateOverrideRow(t) {
  return {
    templateId: t?.template_id ?? t?.templateId ?? t?.id ?? null,
    name: t?.template_name ?? t?.name ?? 'Untitled template',
    dealershipCount: num(t?.dealership_count),
    overrideCount: num(t?.override_count),
    retailCount: num(t?.retail_count),
    wholesaleCount: num(t?.wholesale_count),
    isMobile: !!(t?.is_mobile),
    // Left as raw rows — shape unobserved. describeOverriddenCells below
    // tolerates any entry missing the fields it looks for.
    cells: Array.isArray(t?.cells) ? t.cells : [],
  };
}

// Intentional duplicate of catalogValidation.js's maps — see file header.
const SIZE_LABELS = { XL: 'X-Large', L: 'Large', M: 'Medium', S: 'Small' };
const EDIT_SIZE_ORDER = ['XL', 'L', 'M', 'S'];
const CATALOG_LABELS = { RETAIL: 'Retail', WHOLE: 'Wholesale' };

/**
 * Which sizes/catalogs a template deviates on, from the unobserved `cells`
 * payload. Every field is read defensively — an entry missing a recognizable
 * size or catalog is skipped rather than thrown on, and a `cells` array that
 * yields nothing describable returns null so the caller falls back to the
 * counts-only row instead of an empty/broken line.
 */
export function describeOverriddenCells(cells) {
  if (!Array.isArray(cells) || cells.length === 0) return null;

  const bySize = new Map(); // sizeLabel -> Set<catalogLabel>
  cells.forEach((cell) => {
    if (!cell) return;
    const rawSize = cell.vehicle_size ?? cell.size ?? cell.vehicleSize;
    const rawCatalog = cell.catalog ?? cell.catalog_type ?? cell.catalogType;
    if (!rawSize && !rawCatalog) return;

    const sizeLabel = rawSize ? (SIZE_LABELS[String(rawSize).toUpperCase()] || String(rawSize)) : 'Unspecified size';
    const catalogLabel = rawCatalog
      ? (CATALOG_LABELS[String(rawCatalog).toUpperCase()] || String(rawCatalog))
      : null;

    if (!bySize.has(sizeLabel)) bySize.set(sizeLabel, new Set());
    if (catalogLabel) bySize.get(sizeLabel).add(catalogLabel);
  });

  if (bySize.size === 0) return null;

  const sizeOrder = [...EDIT_SIZE_ORDER.map((s) => SIZE_LABELS[s]), 'Unspecified size'];
  const ordered = [...bySize.keys()].sort((a, b) => {
    const ai = sizeOrder.indexOf(a);
    const bi = sizeOrder.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  return ordered
    .map((size) => {
      const catalogs = [...bySize.get(size)];
      return catalogs.length > 0 ? `${size} (${catalogs.join(', ')})` : size;
    })
    .join(', ');
}
